/**
 * @module PassCheckList
 */

import { Check, X } from "phosphor-react";
import { useTranslation } from "react-i18next";

/** @public */
export type Length = number;

/** @public */
export type Contains = string[];

/**
 * Props for the PassCheckList component
 * @public
 */
export interface PassCheckListProps {
  /** Array of password criteria that are currently met */
  contains: Contains;
  /** Current password length */
  length: Length;
}

/**
 * Component that displays password requirements checklist
 * @remarks Shows visual indicators for different password criteria:
 * - Minimum length (8 characters)
 * - Uppercase letters
 * - Lowercase letters
 * - Numbers
 * - Special characters
 * 
 * @param {PassCheckListProps} props - Component properties
 * @param {Contains} props.contains - Array of met password criteria
 * @param {Length} props.length - Current password length
 * @returns {JSX.Element} Password requirements checklist with visual indicators
 */
export function PassCheckList({ contains, length }: PassCheckListProps) {
  const { t } = useTranslation();
    
  return (
    <div className="text-xs flex gap-2 flex-col mb-3 mt-2">      
      <ul className="flex gap-1.5 flex-col mt-2">
        <li className="flex items-center gap-2">
          <span
            className={`w-3 h-3 rounded-full flex items-center justify-center ${
              length >= 6
                ? "bg-[var(--color-green-500)]"
                : "bg-[var(--color-zinc-300)]"
            }`}
          >
            {length >= 6 ? (
              <Check size={8} className="text-white" />
            ) : (
              <X size={8} color="white" weight="duotone" />
            )}
          </span>
          <span
            className={
              length >= 6
                ? "text-[var(--color-zinc-600)]"
                : "text-[var(--color-zinc-300)]"
            }
          >
            {t("auth.min-length-requirement")}
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span
            className={`w-3 h-3 rounded-full flex items-center justify-center ${
              contains.includes("uppercase")
                ? "bg-[var(--color-green-500)]"
                : "bg-[var(--color-zinc-300)]"
            }`}
          >
            {contains.includes("uppercase") ? (
              <Check size={8} color="white" />
            ) : (
              <X size={8} color="white" />
            )}
          </span>
          <span
            className={
              contains.includes("uppercase")
                ? "text-[var(--color-zinc-600)]"
                : "text-[var(--color-zinc-300)]"
            }
          >
            {t("auth.min-uppercase-requirement")}
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span
            className={`w-3 h-3 rounded-full flex items-center justify-center ${
              contains.includes("number")
                ? "bg-[var(--color-green-500)]"
                : "bg-[var(--color-zinc-300)]"
            }`}
          >
            {contains.includes("number") ? (
              <Check size={8} color="white" />
            ) : (
              <X size={8} color="white" />
            )}
          </span>
          <span
            className={
              contains.includes("number")
                ? "text-[var(--color-zinc-600)]"
                : "text-[var(--color-zinc-300)]"
            }
          >
            {t("auth.min-number-requirement")}
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span
            className={`w-3 h-3 rounded-full flex items-center justify-center ${
              contains.includes("symbol")
                ? "bg-[var(--color-green-500)]"
                : "bg-[var(--color-zinc-300)]"
            }`}
          >
            {contains.includes("symbol") ? (
              <Check size={8} color="white" />
            ) : (
              <X size={8} color="white" />
            )}
          </span>
          <span
            className={
              contains.includes("symbol")
                ? "text-[var(--color-zinc-600)]"
                : "text-[var(--color-zinc-300)]"
            }
          >
            {t("auth.min-special-char-requirement")}
          </span>
        </li>
      </ul>
    </div>
  );
}
